/**
 * Backend Logger Utility (Node.js compatible)
 * Environment-aware logging for Vercel serverless functions
 * Automatically suppresses DEBUG/INFO logs in production
 */

// Log levels
const LogLevel = {
    DEBUG: 0,
    INFO: 1,
    WARN: 2,
    ERROR: 3,
    NONE: 4
};

/**
 * Get environment-based logging configuration
 */
const getEnvironmentLogConfig = () => {
    // Check various environment variables
    const environment = process.env.VITE_ENVIRONMENT ||
        process.env.ENVIRONMENT ||
        process.env.NODE_ENV ||
        'development';

    const customLogLevel = process.env.VITE_LOG_LEVEL || process.env.LOG_LEVEL;

    // If custom log level is specified, use it
    if (customLogLevel) {
        const levelMap = {
            'DEBUG': LogLevel.DEBUG,
            'INFO': LogLevel.INFO,
            'WARN': LogLevel.WARN,
            'ERROR': LogLevel.ERROR,
            'NONE': LogLevel.NONE
        };
        const level = levelMap[customLogLevel.toUpperCase()] || LogLevel.INFO;
        return {
            minLevel: level,
            enabled: level !== LogLevel.NONE,
            environment: environment
        };
    }

    // Environment-based defaults
    const envLower = environment.toLowerCase();

    if (envLower === 'prod' || envLower === 'production') {
        return {
            minLevel: LogLevel.ERROR,
            enabled: true,
            environment: environment
        };
    } else if (envLower === 'dev' || envLower === 'development') {
        return {
            minLevel: LogLevel.INFO,
            enabled: true,
            environment: environment
        };
    } else {
        // local, localhost, or default
        return {
            minLevel: LogLevel.DEBUG,
            enabled: true,
            environment: environment
        };
    }
};

// Get configuration once at module load
const config = getEnvironmentLogConfig();

/**
 * Check if logging is enabled for the given level
 */
const isLoggingEnabled = (level) => {
    return config.enabled && level >= config.minLevel;
};

/**
 * Redact sensitive data from URLs (API keys, tokens)
 */
const redactUrl = (url) => {
    if (!url || typeof url !== 'string') return url;

    // Redact API keys in URLs (e.g., /v2/API_KEY or ?apikey=API_KEY)
    return url
        .replace(/\/v2\/[A-Za-z0-9_-]{20,}/g, '/v2/[REDACTED]')
        .replace(/\/v3\/[A-Za-z0-9_-]{20,}/g, '/v3/[REDACTED]')
        .replace(/apikey=[A-Za-z0-9_-]{20,}/gi, 'apikey=[REDACTED]')
        .replace(/api_key=[A-Za-z0-9_-]{20,}/gi, 'api_key=[REDACTED]')
        .replace(/token=[A-Za-z0-9_-]{20,}/gi, 'token=[REDACTED]');
};

/**
 * Redact sensitive information from arguments
 */
const redactSensitiveInfo = (args) => {
    return args.map(arg => {
        if (typeof arg === 'string') {
            return redactUrl(arg);
        } else if (typeof arg === 'object' && arg !== null) {
            // Redact sensitive fields in objects
            try {
                const copy = JSON.parse(JSON.stringify(arg));
                const sensitiveFields = ['apiKey', 'api_key', 'token', 'secret', 'password', 'private_key'];

                const redactObject = (obj) => {
                    if (typeof obj !== 'object' || obj === null) return;

                    Object.keys(obj).forEach(key => {
                        if (sensitiveFields.some(field => key.toLowerCase().includes(field))) {
                            obj[key] = '[REDACTED]';
                        } else if (typeof obj[key] === 'string') {
                            obj[key] = redactUrl(obj[key]);
                        } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                            redactObject(obj[key]);
                        }
                    });
                };

                redactObject(copy);
                return copy;
            } catch (e) {
                return arg;
            }
        }
        return arg;
    });
};

/**
 * Backend logger object
 */
const logger = {
    /**
     * Log a debug message (suppressed in production)
     */
    debug: (...args) => {
        if (isLoggingEnabled(LogLevel.DEBUG)) {
            console.debug(...redactSensitiveInfo(args));
        }
    },

    /**
     * Log an info message (suppressed in production)
     */
    info: (...args) => {
        if (isLoggingEnabled(LogLevel.INFO)) {
            console.info(...redactSensitiveInfo(args));
        }
    },

    /**
     * Log a warning message
     */
    warn: (...args) => {
        if (isLoggingEnabled(LogLevel.WARN)) {
            console.warn(...redactSensitiveInfo(args));
        }
    },

    /**
     * Log an error message
     */
    error: (...args) => {
        if (isLoggingEnabled(LogLevel.ERROR)) {
            console.error(...redactSensitiveInfo(args));
        }
    },

    /**
     * Log a message (alias for info)
     */
    log: (...args) => {
        if (isLoggingEnabled(LogLevel.INFO)) {
            console.log(...redactSensitiveInfo(args));
        }
    },

    /**
     * Get the current logger configuration
     */
    getConfig: () => ({ ...config }),
};

// Export for ES modules only
export default logger;
export { LogLevel };
